import { IOptionsStorage, ITable } from "@tedivo/tedivo-smart-table";
import {
  IOrganizationsCognitoAttributeAugmented,
  systemRights,
} from "@tedivo/tvd-api-models";

import { ICognitoUserWithLastLoginDate } from "../../../app/services/admin/organizations";
import { ListComponentWithActions } from "../../common/ListComponentWithActions";
import Services from "../../../app/services";
import { SlButton } from "@shoelace-style/shoelace";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import { formatDateInCurrentTimezone } from "@tedivo/tedivo-pure-helpers";
import { getTranslation } from "../../../app/i18/i18tn";
import goSquared from "../../../app/tracking/goSquared";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import { setAppTitle } from "../../../app/setAppTitle";

export class TVDAdminUsersComponent extends ListComponentWithActions<ICognitoUserWithLastLoginDate> {
  public static observedAttributes = [];
  private exportButton: SlButton;

  constructor() {
    super();

    this.h1Label = "general:users.users";
    this.goSquaredLabel = "Admin/Users";
    this.topLabel = "general:adminArea";
    this.deleteLabel = "general:users.delete";

    this.serviceListCall = Services.organizations.adminGetAllUsers;

    this.systemRightToEdit = systemRights.ORG.AppAdminCrudOrganizationUser;

    const userOptionsStorage =
      createTableUserStorageInstance<ICognitoUserWithLastLoginDate>(
        "adminUsersList",
      );

    const exportButton = document.createElement("sl-button");
    exportButton.className = "tvd-button";
    exportButton.innerHTML = getTranslation("general:common.exportToXlsx");
    exportButton.variant = "primary";
    exportButton.addEventListener("click", () => {
      this.exportToExcelFn();
    });
    this.exportButton = exportButton;

    this.tableConfig = (data: ICognitoUserWithLastLoginDate[]) =>
      createUsersTableConfig(data, userOptionsStorage, exportButton);

    setAppTitle(getTranslation("general:users.users"));
    goSquared.trackPage("TEDIVO Admin Pages - Users");
  }

  exportToExcelFn = () => {
    const usersData = this.smartTable?.sortedData;
    if (!usersData) return;

    this.exportButton.loading = true;
    this.exportButton.disabled = true;

    import(/* webpackChunkName: "exports1" */ "./exportUsersToExcel")
      .then((module) => {
        module.exportUsersToExcel(usersData);
      })
      .finally(() => {
        this.exportButton.loading = false;
        this.exportButton.disabled = false;
      });
  };
}

customElements.define("tvd-admin-users-component", TVDAdminUsersComponent);

declare global {
  interface HTMLElementTagNameMap {
    "tvd-admin-users-component": TVDAdminUsersComponent;
  }
}

function createUsersTableConfig(
  usersData: ICognitoUserWithLastLoginDate[],
  userOptionsStorage: IOptionsStorage<ICognitoUserWithLastLoginDate>,
  exportButton: SlButton,
): ITable<ICognitoUserWithLastLoginDate> {
  const actionBox = document.createElement("div");
  actionBox.appendChild(exportButton);

  const tableConfig: ITable<ICognitoUserWithLastLoginDate> = {
    title: getTranslation("general:users.adminList"),
    className: "tvd-table tvd-table-smart-table",
    data: usersData,
    defaultSortBy: "name",
    fields: [
      {
        name: "name",
        label: getTranslation("general:common.name"),
      },
      {
        name: "familyName",
        label: getTranslation("general:users.familyName"),
      },
      {
        name: "email",
        label: getTranslation("general:common.email"),
      },
      {
        name: "organizations",
        label: getTranslation("general:organizations.organizations"),
        sortValueFunction: (dta) =>
          (dta.organizations as IOrganizationsCognitoAttributeAugmented[])
            .map((o) => o.name)
            .sort()
            .join(","),
        mapper: (dta) => {
          return createOrgsNode(dta);
        },
      },
      {
        name: "userStatusType",
        label: getTranslation("general:common.status"),
        mapper: (u) =>
          getTranslation(`enums:UserStatusType.${u.userStatusType}`),
        className: (u) =>
          u?.userStatusType === "EXTERNAL_PROVIDER" ? "state-highlighted" : "",
      },
      {
        name: "userCreateDate",
        label: getTranslation("general:common.createdAt"),
        valueFunction: (dta) =>
          dta.userCreateDate
            ? formatDateInCurrentTimezone(dta.userCreateDate)
            : "",
        className: "centered",
      },
      {
        name: "userLastModifiedDate",
        label: getTranslation("general:common.modifiedAt"),
        valueFunction: (dta) =>
          dta.userLastModifiedDate
            ? formatDateInCurrentTimezone(dta.userLastModifiedDate)
            : "",

        className: "centered",
      },
      {
        name: "lastLoginDate",
        label: getTranslation("general:users.lastLoginDate"),
        valueFunction: (dta) =>
          dta.lastLoginDate
            ? formatDateInCurrentTimezone(dta.lastLoginDate)
            : "",

        className: "centered",
      },
    ],
    settings: {
      sortEnabled: true,
      sortVisible: true,
      userOptionsStorage,
      filters: {
        textFieldEnabled: true,
        textFieldFilterFields: ["name", "familyName", "organizations"],
        initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
      },
      labelSortBy: getTranslation("general:common.sortBy"),
      labelSearch: getTranslation("general:common.filterBy"),
      actionBox,
    },
    pkFunction: (dta) => dta.sub,
  };

  return tableConfig;
}

function createOrgsNode(dta: ICognitoUserWithLastLoginDate) {
  const div = document.createElement("div");
  const orgs = dta.organizations as
    | IOrganizationsCognitoAttributeAugmented[]
    | undefined;

  if (!orgs) return div;

  orgs
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((r) => {
      const h = document.createElement("div");
      h.className = "parent-orgs-row";

      const span = document.createElement("span");
      span.innerHTML = r.name;
      if (!r.uEnabled) span.classList.add("disabled");
      const small = document.createElement("small");
      small.innerHTML = r.role;
      span.appendChild(document.createTextNode(" "));
      span.appendChild(small);

      const editOrgIcon = document.createElement("sl-icon-button");
      editOrgIcon.name = "pencil";
      editOrgIcon.title = `Edit ${r.name}`;
      editOrgIcon.addEventListener("click", () => {
        router.navigate(routeFns.adminOrganizations(), {
          organizationId: r.orgId,
          returnUrl: routeFns.adminUsers(),
        });
      });

      const initials =
        (dta.name[0] || "").toUpperCase() +
        (dta.familyName[0] || "").toUpperCase();

      const editUsersIcon = document.createElement("sl-icon-button");
      editUsersIcon.name = "person-gear";
      editUsersIcon.title = `Edit ${initials} in ${r.name}`;
      editUsersIcon.addEventListener("click", () => {
        router.navigate(routeFns.adminOrganizationUsers(r.orgId), {
          userMainIdentity: dta.mainIdentityId,
          returnUrl: routeFns.adminUsers(),
        });
      });

      const goToPeoplePageIcon = document.createElement("sl-icon-button");
      goToPeoplePageIcon.name = "people";
      goToPeoplePageIcon.title = `Go to ${r.name} people page`;
      goToPeoplePageIcon.addEventListener("click", () => {
        router.navigate(routeFns.adminOrganizationUsers(r.orgId), {
          returnUrl: routeFns.adminUsers(),
        });
      });

      h.appendChild(span);
      h.appendChild(editOrgIcon);
      h.appendChild(editUsersIcon);
      h.appendChild(goToPeoplePageIcon);

      div.appendChild(h);
    });

  return div;
}
